import { nextTick } from 'vue';
import MarkdownIt from 'markdown-it';
import renderMathInElement from 'katex/contrib/auto-render';
import 'katex/dist/katex.min.css';

// Initialize markdown-it with HTML support (for tables)
const md = new MarkdownIt({
    html: true, // Enable HTML tags in source
    breaks: true, // Convert '\n' in paragraphs into <br>
    linkify: true, // Auto-convert URLs to links
    typographer: true, // Enable smartquotes and other typographic replacements
});

// KaTeX options
const katexOptions = {
    delimiters: [
        { left: '$$', right: '$$', display: true },
        { left: '\\[', right: '\\]', display: true },
        { left: '\\(', right: '\\)', display: false },
        { left: '$', right: '$', display: false },
    ],
    throwOnError: false,
    errorCallback: (msg, err) => {
        console.error('KaTeX error:', msg, err);
    },
};

/**
 * Protects LaTeX expressions from markdown processing
 * @param {string} text - Text with LaTeX
 * @returns {object} - { protectedText: string, placeholders: Map }
 */
const protectLatex = (text) => {
    const placeholders = new Map();
    let counter = 0;

    // Protect display math ($$...$$)
    let protectedText = text.replace(/\$\$([\s\S]*?)\$\$/g, (match) => {
        const id = `LATEXPLACEHOLDERDISPLAY${counter++}ENDPLACEHOLDER`;
        placeholders.set(id, match);
        return id;
    });

    // Protect display math (\[...\])
    protectedText = protectedText.replace(/\\\[([\s\S]*?)\\\]/g, (match) => {
        const id = `LATEXPLACEHOLDERDISPLAY${counter++}ENDPLACEHOLDER`;
        placeholders.set(id, match);
        return id;
    });

    // Protect inline math (\(...\))
    protectedText = protectedText.replace(/\\\(([\s\S]*?)\\\)/g, (match) => {
        const id = `LATEXPLACEHOLDERINLINE${counter++}ENDPLACEHOLDER`;
        placeholders.set(id, match);
        return id;
    });

    // Protect inline math ($...$) - must be careful not to match display math
    protectedText = protectedText.replace(/\$([^$\n]+?)\$/g, (match) => {
        const id = `LATEXPLACEHOLDERINLINE${counter++}ENDPLACEHOLDER`;
        placeholders.set(id, match);
        return id;
    });

    return { protectedText, placeholders };
};

/**
 * Restores LaTeX expressions after markdown processing
 * @param {string} text - Text with placeholders
 * @param {Map} placeholders - Map of placeholders to LaTeX
 * @returns {string} - Text with LaTeX restored
 */
const restoreLatex = (text, placeholders) => {
    let restored = text;
    for (const [id, latex] of placeholders.entries()) {
        // Use replaceAll to replace all occurrences of the placeholder
        restored = restored.replaceAll(id, latex);
    }
    return restored;
};

/**
 * Preprocesses text to handle LaTeX lists and escape sequences
 * before markdown processing
 */
const preprocessText = (text) => {
    if (!text) return '';

    // Preprocessing: Move inline numbered items to their own lines
    text = text.replace(/\.\s+(\d+)\.\s+/g, '.\n$1. ');

    return text
        // LaTeX lists → HTML lists (KaTeX doesn't support itemize/enumerate)
        .replace(/\\begin\{itemize\}\s*/g, '<ul>')
        .replace(/\\begin\{enumerate\}\s*/g, '<ol>')
        .replace(/\s*\\end\{itemize\}/g, '</li></ul>')
        .replace(/\s*\\end\{enumerate\}/g, '</li></ol>')
        .replace(/\\item\s*/g, '</li><li>')
        .replace(/<ul><\/li>/g, '<ul>') // Clean first empty </li>
        .replace(/<ol><\/li>/g, '<ol>') // Clean first empty </li>
        // Escape sequences
        .replace(/\\r\\n/g, '<br>') // Windows line endings
        .replace(/\\r(?![a-z])/g, '') // Stray \r (keeps \rightarrow, \rho)
        .replace(/\\n(?![a-z])/g, '<br>') // Newlines (keeps \newline, \nu, \neq)
        .replace(/\\t(?![a-z])/g, '&nbsp;&nbsp;&nbsp;&nbsp;'); // Tabs (keeps \text, \times)
};

/**
 * Formats text with markdown and LaTeX support
 * @param {string} text - The text to format
 * @returns {string} - HTML formatted text
 */
export const formatText = (text) => {
    if (!text) return '';

    // Step 1: Protect LaTeX from markdown processing
    const { protectedText, placeholders } = protectLatex(text);

    // Step 2: Preprocess for LaTeX lists and escape sequences
    const preprocessed = preprocessText(protectedText);

    // Step 3: Render markdown to HTML
    const markdownRendered = md.render(preprocessed);

    // Step 4: Restore LaTeX expressions
    return restoreLatex(markdownRendered, placeholders);
};

/**
 * Renders KaTeX math in a DOM element
 * @param {HTMLElement} element - The element to render math in
 */
export const renderMath = (element) => {
    if (element) {
        renderMathInElement(element, katexOptions);
    }
};

/**
 * Renders KaTeX math in a DOM element after a delay
 * Useful for dynamically rendered content
 */
export const renderMathAsync = async (element) => {
    await nextTick();
    await new Promise(r => setTimeout(r, 0));
    renderMath(element);
};

/**
 * Composable for markdown rendering with KaTeX support
 */
export const useMarkdownRenderer = () => {
    return {
        formatText,
        renderMath,
        renderMathAsync,
        katexOptions,
    };
};
